import React, {FC, useEffect, useMemo, useState} from 'react';
import {
    RecognizableAndTranslatableLanguage,
    SessionStates,
    TranslatableAndSpeakableLanguage
} from '../contexts/Configuration';
import {buildLanguageMatrixV2} from '../contexts/SessionContext';
import {Autocomplete, TextField} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {useAppDispatch, useAppSelector} from '../hooks/storeHooks';
import {updateSessionState} from '../reducers/sessionReducer';
import './LanguageSelector.scss';

const ICON_SLOT_WIDTH = 44;

const LanguageSelector: FC<LanguageSelectorProps> = ({
                                                         id,
                                                         isInputSelector,
                                                         onLanguageSelected,
                                                         withSessionIcons = false
                                                     }) => {

    const started = useAppSelector((state) => state.session.sessionStarted);
    const sessionState = useAppSelector((state) => state.session.sessionState);

    const preSelectedInputLanguage = useAppSelector((state) => state.session.preSelectedInputLanguage);
    const preSelectedOutputLanguages = useAppSelector((state) => state.session.preSelectedOutputLanguages);
    const autoDetectLang = useAppSelector((state) => state.session.autoDetectLang);
    const input = useAppSelector((state) => state.session.input);
    const outputs = useAppSelector((state) => state.session.outputs);

    const dispatch = useAppDispatch();

    const [inputOptions, setInputOptions] = useState<RecognizableAndTranslatableLanguage[]>([])
    const [outputOptions, setOutputOptions] = useState<TranslatableAndSpeakableLanguage[]>([])

    useEffect(() => {
        buildLanguageMatrixV2()
            .then((results: { a: RecognizableAndTranslatableLanguage[], b: TranslatableAndSpeakableLanguage[] }) => {
                setInputOptions(results.a);
                setOutputOptions(results.b);

                dispatch(updateSessionState({state: SessionStates.OFF}));

                if (isInputSelector && !!preSelectedInputLanguage) {
                    const preSelected = results.a
                        .find(l => l.recognitionKey === preSelectedInputLanguage);
                    if (!!preSelected) {
                        onLanguageSelected(preSelected);
                    }
                }

                if (!isInputSelector && !!preSelectedOutputLanguages && !!preSelectedOutputLanguages[id] && preSelectedOutputLanguages[id].includes(':')) {
                    const preSelectedKeys = preSelectedOutputLanguages[id].split(':');
                    if (preSelectedKeys.length === 2) {
                        const preSelected = results.b
                            .find(l => l.translationKey === preSelectedKeys[0] &&
                                l.speakKey === preSelectedKeys[1]
                            );
                        if (!!preSelected) {
                            onLanguageSelected(preSelected);
                        }
                    }
                }
            });
    }, []);

    const label = `${isInputSelector ? 'Input' : 'Output'} language`;
    const automaticDetectionLabel = 'Automatic detection';

    const outputLangSelected = useMemo(() => {
        return outputs?.find(o => o.id === id)?.language || null;
    }, [outputs, id]);

    // Languages already chosen by other output panels — hide them from this panel's options so the
    // same language cannot be selected twice (duplicate outputs are otherwise dropped at session start).
    const availableOutputOptions = useMemo(() => {
        const takenByOtherPanels = new Set(
            (outputs || [])
                .filter(o => o.id !== id && o.language?.translationKey)
                .map(o => o.language.translationKey)
        );
        return outputOptions.filter(op => !takenByOtherPanels.has(op.translationKey));
    }, [outputOptions, outputs, id]);

    const showSessionIcons = withSessionIcons && started;

    const micBadge = (
        <div className="lang-selector-badge-slot">
            <span className={`lang-selector-badge-circle${started ? ' lang-selector-badge-circle--active-mic' : ''}`}>
                <MicIcon className={`lang-selector-badge-icon ${started ? 'lang-selector-badge-icon--mic-active' : 'lang-selector-badge-icon--mic'}`}/>
            </span>
        </div>
    );

    const speakerBadge = (
        <div className="lang-selector-badge-slot">
            <span className="lang-selector-badge-circle">
                <VolumeUpIcon className="lang-selector-badge-icon lang-selector-badge-icon--speaker"/>
            </span>
        </div>
    );

    if (isInputSelector) {
        return (
            <div className="lang-selector-wrapper">
                <Autocomplete
                    id="selectableLanguages"
                    filterSelectedOptions
                    loading={outputOptions.length === 0 || sessionState === SessionStates.LOADING}
                    loadingText="Loading Languages"
                    disabled={started || autoDetectLang}
                    className="lang-selector-container-input"
                    options={inputOptions}
                    getOptionLabel={op => autoDetectLang ? automaticDetectionLabel : op.name}
                    isOptionEqualToValue={(option, value) => {
                        return value !== undefined && value !== null &&
                            option.recognitionKey === value.recognitionKey;
                    }}
                    value={input?.language || null}
                    onChange={(_e, newValue) => {
                        onLanguageSelected(newValue)
                    }}
                    sx={{width: showSessionIcons ? `calc(100% - ${ICON_SLOT_WIDTH}px)` : '100%'}}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={autoDetectLang ? automaticDetectionLabel : label}
                        />
                    )}
                />
                {showSessionIcons && micBadge}
            </div>
        );
    }

    return (
        <div className="lang-selector-wrapper">
            <Autocomplete
                id="selectableLanguageOptions"
                filterSelectedOptions
                loading={outputOptions.length === 0}
                loadingText="Loading Languages"
                disabled={started}
                className="lang-selector-container"
                options={availableOutputOptions}
                getOptionLabel={op => op.name}
                isOptionEqualToValue={(option, value) => {
                    return value !== undefined && value !== null &&
                        option.translationKey === value.translationKey && option.speakKey === value.speakKey;
                }}
                value={outputLangSelected}
                onChange={(_e, newValue) => {
                    onLanguageSelected(newValue);
                }}
                sx={{width: showSessionIcons ? `calc(100% - ${ICON_SLOT_WIDTH}px)` : '100%'}}
                renderInput={(params) =>
                    <TextField {...params} size="small" placeholder={label}/>
                }
            />
            {showSessionIcons && speakerBadge}
        </div>
    );
}

type LanguageSelectorProps = {
    id: string,
    isInputSelector: boolean,
    onLanguageSelected: (selected: RecognizableAndTranslatableLanguage | TranslatableAndSpeakableLanguage | null) => void;
    withSessionIcons?: boolean;
}

export default LanguageSelector
